import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { defaultLocale } from "../utils/locales";
import marketdata from "../data/Services/marketdata";
import { createScopedStorage } from "./workspaceStore";
import { getSettingsKey } from "../workspace/constants";

// Settings store for application configuration
// Now synced with marketdata server with localStorage fallback
export const useSettingsStore = create(
  persist(
    (set, get) => ({
      // Configuration settings with defaults
      confirmOrders: true,
      routingSounds: true, // Default: enabled (routing sounds on by default)
      oneClickTrading: true, // Default: enabled (as requested)
      showGrid: true, // Global grid setting
      showIndicatorLabels: true, // Default: enabled (show indicator value labels on Y-axis)
      zoomButtonsAlwaysVisible: false, // Default: disabled (show -, reset, + only on hover)
      barCountdownVisibilityMode: "hover", // always | never | hover
      priceChangeVisibilityMode: "hover", // always | never | hover
      yAxisDrag: true, // Default: enabled (Y-axis drag to change scale proportions)
      crosshairWidth: 3, // Default: 3 (crosshair line width: 1, 3, or 5)
      modifierKeyDisarming: false, // Default: disabled (no restriction on rapid orders with modifier keys)
      locale: defaultLocale, // Default: pt-BR (Portuguese Brazil)
      autoShowLoadDesktopDialog: true, // Default: enabled (show load desktop dialog on startup when multiple desktops exist)
      keepScreenAwake: true, // Default: enabled (prevent screen from dimming/locking via Wake Lock API)
      showIchimokuFutureProjection: true, // Default: enabled (issue #161 — render 26-bar future cloud projection past last candle)
      continuousObjectInsertion: false, // Default: disabled (issue #305 — keep drawing tool active after placing an object)
      defaultPanelToolbarPinned: true, // Default: new panel toolbars start pinned/reserving space
      tipOfTheDayEnabled: true, // Default: enabled (rotating Tip of the Day toast, once per session)
      oneLineNotifications: false, // Default: disabled (issue #430 — compact one-line toasts so bursts don't cover the SuperDOM book)
      showMyDesktopsBar: true, // Default: enabled ("Meus Desktops" top bar; also gated by the myDesktops feature flag)
      favoriteDesktops: [], // Desktop ids (server chartid) starred by the user — synced to the server so favorites follow the user

      // Loading state
      isLoading: false,
      isInitialized: false,

      /**
       * Load settings from marketdata server
       */
      loadFromServer: async () => {
        if (!marketdata.user) {
          console.log("[Settings] No user logged in, using localStorage only");
          set({ isInitialized: true });
          return;
        }

        set({ isLoading: true });
        try {
          const data = await marketdata.getSettings();
          if (data) {
            // Merge server settings with current state (server takes priority)
            const serverSettings =
              typeof data === "string" ? JSON.parse(data) : data;
            set({ ...serverSettings, isInitialized: true });
            console.log("[Settings] Loaded from server");
          } else {
            console.log("[Settings] No settings on server, using localStorage");
            set({ isInitialized: true });
          }
        } catch {
          console.log(
            "[Settings] Could not load from server, using localStorage",
          );
          set({ isInitialized: true });
        } finally {
          set({ isLoading: false });
        }
      },

      /**
       * Save settings to marketdata server
       */
      saveToServer: async () => {
        if (!marketdata.user) {
          console.log(
            "[Settings] No user logged in, saving to localStorage only",
          );
          return;
        }

        const settings = Object.fromEntries(
          Object.entries(get()).filter(
            ([key, value]) =>
              typeof value !== "function" &&
              key !== "isLoading" &&
              key !== "isInitialized",
          ),
        );
        try {
          const content = JSON.stringify(settings);
          await marketdata.saveSettings(content);
          console.log("[Settings] Saved to server successfully");
        } catch {
          console.warn(
            "[Settings] Could not save to server, saved to localStorage only",
          );
        }
      },

      // Actions to update settings
      setConfirmOrders: async (enabled) => {
        set({ confirmOrders: enabled });
        await get().saveToServer();
      },
      setRoutingSounds: async (enabled) => {
        set({ routingSounds: enabled });
        await get().saveToServer();
      },
      setOneClickTrading: async (enabled) => {
        set({ oneClickTrading: enabled });
        await get().saveToServer();
      },
      setShowGrid: async (enabled) => {
        set({ showGrid: enabled });
        await get().saveToServer();
      },
      setShowIndicatorLabels: async (enabled) => {
        set({ showIndicatorLabels: enabled });
        await get().saveToServer();
      },
      setZoomButtonsAlwaysVisible: async (enabled) => {
        set({ zoomButtonsAlwaysVisible: enabled });
        await get().saveToServer();
      },
      setBarCountdownVisibilityMode: async (mode) => {
        set({ barCountdownVisibilityMode: mode });
        await get().saveToServer();
      },
      setPriceChangeVisibilityMode: async (mode) => {
        set({ priceChangeVisibilityMode: mode });
        await get().saveToServer();
      },
      setYAxisDrag: async (enabled) => {
        set({ yAxisDrag: enabled });
        await get().saveToServer();
      },
      setCrosshairWidth: async (width) => {
        set({ crosshairWidth: width });
        await get().saveToServer();
      },
      setModifierKeyDisarming: async (enabled) => {
        set({ modifierKeyDisarming: enabled });
        await get().saveToServer();
      },
      setLocale: async (locale) => {
        set({ locale });
        await get().saveToServer();
      },
      setAutoShowLoadDesktopDialog: async (enabled) => {
        set({ autoShowLoadDesktopDialog: enabled });
        await get().saveToServer();
      },
      setKeepScreenAwake: async (enabled) => {
        set({ keepScreenAwake: enabled });
        await get().saveToServer();
      },
      setShowIchimokuFutureProjection: async (enabled) => {
        set({ showIchimokuFutureProjection: enabled });
        await get().saveToServer();
      },
      setContinuousObjectInsertion: async (enabled) => {
        set({ continuousObjectInsertion: enabled });
        await get().saveToServer();
      },
      setDefaultPanelToolbarPinned: async (enabled) => {
        set({ defaultPanelToolbarPinned: enabled });
        await get().saveToServer();
      },
      setTipOfTheDayEnabled: async (enabled) => {
        set({ tipOfTheDayEnabled: enabled });
        await get().saveToServer();
      },
      setShowMyDesktopsBar: async (enabled) => {
        set({ showMyDesktopsBar: enabled });
        await get().saveToServer();
      },
      setOneLineNotifications: async (enabled) => {
        set({ oneLineNotifications: enabled });
        await get().saveToServer();
      },
      toggleFavoriteDesktop: async (id) => {
        const current = get().favoriteDesktops || [];
        const favoriteDesktops = current.includes(id)
          ? current.filter((f) => f !== id)
          : [...current, id];
        set({ favoriteDesktops });
        await get().saveToServer();
      },
      removeFavoriteDesktop: async (id) => {
        const current = get().favoriteDesktops || [];
        if (!current.includes(id)) return;
        set({ favoriteDesktops: current.filter((f) => f !== id) });
        await get().saveToServer();
      },

      // Bulk update settings
      updateSettings: async (newSettings) => {
        set(newSettings);
        await get().saveToServer();
      },

      // Reset settings to defaults
      resetSettings: async () => {
        set({
          confirmOrders: true,
          routingSounds: true,
          oneClickTrading: true,
          showGrid: true,
          showIndicatorLabels: true,
          zoomButtonsAlwaysVisible: false,
          barCountdownVisibilityMode: "hover",
          priceChangeVisibilityMode: "hover",
          yAxisDrag: true,
          crosshairWidth: 3,
          modifierKeyDisarming: false,
          locale: defaultLocale,
          autoShowLoadDesktopDialog: true,
          keepScreenAwake: true,
          showIchimokuFutureProjection: true,
          continuousObjectInsertion: false,
          defaultPanelToolbarPinned: true,
          tipOfTheDayEnabled: true,
          oneLineNotifications: false,
          showMyDesktopsBar: true,
          favoriteDesktops: [],
        });
        await get().saveToServer();
      },
    }),
    {
      name: "odin-settings", // Used as fallback name, actual key is dynamic (user-scoped)
      storage: createJSONStorage(() => createScopedStorage(getSettingsKey)),
      version: 16,
      migrate: (persistedState, version) => {
        let state = { ...persistedState };

        // Handle migration from version 1 to 2 (added locale field)
        if (version < 2) {
          state.locale = defaultLocale;
        }

        // Handle migration from version 2 to 3 (added showIndicatorLabels field)
        if (version < 3) {
          state.showIndicatorLabels = true;
        }

        // Handle migration from version 3 to 4 (removed autoRestoreWorkspace - always ON)
        if (version < 4) {
          delete state.autoRestoreWorkspace;
        }

        // Handle migration from version 4 to 5 (added crosshairWidth field)
        if (version < 5) {
          state.crosshairWidth = 3;
        }

        // Handle migration from version 5 to 6 (added modifierKeyDisarming field)
        if (version < 6) {
          state.modifierKeyDisarming = false;
        }

        // Handle migration from version 6 to 7 (added autoShowLoadDesktopDialog field)
        if (version < 7) {
          state.autoShowLoadDesktopDialog = true;
        }

        // Handle migration from version 7 to 8 (added zoomButtonsAlwaysVisible field)
        if (version < 8) {
          state.zoomButtonsAlwaysVisible = false;
        }

        // Handle migration from version 8 to 9 (added bottom indicator visibility modes)
        if (version < 9) {
          state.barCountdownVisibilityMode = "hover";
          state.priceChangeVisibilityMode = "hover";
        }

        // Handle migration from version 9 to 10 (added keepScreenAwake field)
        if (version < 10) {
          state.keepScreenAwake = true;
        }

        // Handle migration from version 10 to 11 (added showIchimokuFutureProjection field — issue #161)
        if (version < 11) {
          state.showIchimokuFutureProjection = true;
        }

        // Handle migration from version 11 to 12 (added continuousObjectInsertion field — issue #305)
        if (version < 12) {
          state.continuousObjectInsertion = false;
        }

        // Handle migration from version 12 to 13 (added default panel toolbar pin state)
        if (version < 13) {
          state.defaultPanelToolbarPinned = true;
        }

        // Handle migration from version 13 to 14 (added tipOfTheDayEnabled field)
        if (version < 14) {
          state.tipOfTheDayEnabled = true;
        }

        // Handle migration from version 14 to 15 ("Meus Desktops" bar + favorites)
        if (version < 15) {
          state.showMyDesktopsBar = true;
          state.favoriteDesktops = [];
        }

        // Handle migration from version 15 to 16 (added oneLineNotifications field — issue #430)
        if (version < 16) {
          state.oneLineNotifications = false;
        }

        return state;
      },
    },
  ),
);

// Expose store on window for E2E tests (mirrors __CHARTS_STORE__ pattern).
// Production code must continue to use useSettingsStore directly.
if (typeof window !== "undefined") {
  window.__SETTINGS_STORE__ = useSettingsStore;
}
